/**
 * Graph API key handling, kept free of heavy imports so utils/graphql.ts (which browsers also load) can use it.
 * The key is read from the server environment at call time; in a browser `process.env.GRAPH_API_KEY` does not exist
 * and every function here answers "no key".
 */
import { Env, gatewayBase, isServerRuntime } from 'config/constants/subgraphs'

/** Shape-checked so a pasted line break or an `export ` prefix can never reach a header. */
export const getGraphApiKey = (env: Env = process.env as Env): string | null => {
  const key = (env.GRAPH_API_KEY || '').trim()
  return /^[A-Za-z0-9_-]{16,128}$/.test(key) ? key : null
}

/** Optional Origin for a key that is restricted to domains in the Studio. Only a bare https origin is accepted. */
export const gatewayOrigin = (env: Env = process.env as Env): string | null => {
  const origin = (env.GRAPH_GATEWAY_ORIGIN || '').trim().replace(/\/+$/, '')
  return /^https:\/\/[a-z0-9.-]+(:\d+)?$/i.test(origin) ? origin : null
}

/**
 * Headers for a request that goes straight to an upstream URL (legacy clients that are handed a plain URL).
 * Only a gateway URL gets the key, and only on the server.
 */
export const authHeadersForUrl = (url: string, env: Env = process.env as Env): Record<string, string> => {
  if (!isServerRuntime() || typeof url !== 'string' || !url.startsWith(`${gatewayBase(env)}/`)) return {}
  const key = getGraphApiKey(env)
  if (!key) return {}
  const origin = gatewayOrigin(env)
  return { authorization: `Bearer ${key}`, ...(origin ? { origin } : {}) }
}
